/**
 * @lume/shared — **Invocador ≠ sujeito**: o MANDATO como primitiva neutra (Motor Neutro B6).
 *
 * Fecha a última primitiva neutra da Frente B. O eixo tem 4 pernas (DRIVER §3: mandato ·
 * escopo · validade · log) e a auditoria de premissas (§0.10) mediu que **três delas já
 * tinham substrato** — a fatia NÃO cria entidade nem migração:
 *
 *   - **mandato**   `BondAuthorizedContact` («quem o titular autorizou a falar em seu nome»)
 *                   e, no eixo IRMÃO da incapacidade, `Guardianship` (B2). O que faltava é o
 *                   conceito NOMEADO — hoje o mandato é um `role` string + um array de scopes.
 *   - **escopo**    já é LIVE no gate (`resolveEffectiveContactScopes` → `roleAllowsAction`).
 *   - **validade**  a coluna existe e a **B3 já lhe atribuiu a polaridade** `expires-forfeits`
 *                   (`typed-deadline.ts` kind `authorized-contact-grant`) — «decorreu ⇒ PERDE
 *                   o direito». Só o **enforcement no ponto de decisão** faltava.
 *   - **log**       inexistente: o único sinal de representação era `intentForm:'on_behalf_of'`,
 *                   que virava PROSA no prompt («respeite o escopo do papel») em vez de fato.
 *
 * ★ **Por que `scopesForfeited` viaja SEPARADO da lista de escopos.** O conserto ingênuo da
 * validade (filtrar o vencido no `where`, como 2 dos 5 leitores fazem) pode **ALARGAR** o
 * acesso: `resolveEffectiveContactScopes` tem semântica de **SUBSTITUIÇÃO**, logo grant
 * AUSENTE cai na política do papel — e se a política for mais larga que o mandato vencido,
 * filtrar concede MAIS. É a lição «fail-safe tem DIREÇÃO» na forma inversa: devolver «não há
 * grant» ELEVA. A polaridade que a B3 declarou resolve sem inventar: caducou ⇒ perde, não
 * volta ao default. Mesmo molde do `selfRestrictedBlanket` da B2c, que existe pela razão
 * idêntica (a restrição mais FORTE produzia o sinal mais FRACO).
 *
 * ORTOGONAL à B2, não sobreposto: a B2 resolve **incapacidade** (ward/guardian + LegalCapacity;
 * `legalCapacity==='full'` ⇒ downgrade a `self`), esta resolve o **mandato VOLUNTÁRIO de sujeito
 * CAPAZ** — que a B2 exclui por construção. A representação legal entra aqui só como **ECO** do
 * `DecisionAuthority` (nunca recalculada), para o log carregar o instrumento correto.
 *
 * Extensibilidade (DECISÃO 1 regra b): `PrincipalRef.kind` já nasce no TIPO para que o
 * **comércio agêntico** (invocador = máquina) seja **ADIÇÃO** de valor (`'agent'`), não
 * reescrita. O valor não entra nesta fatia — `Person` não modela ator não-humano (residual
 * R-B6-a).
 *
 * Fail-safe com DIREÇÃO (DECISÃO 1 regra f — e a lição da B5 «este guard MORDE?»): `now`
 * inválido, `expiresAt` corrompido ⇒ **CADUCA** (nunca concede). Puro/total: sem I/O, sem lançar.
 *
 * Spec: docs/superpowers/specs/2026-07-26-b6-invocador-diferente-do-sujeito-mandato-design.md
 */
/**
 * ⚠️ **NÃO sanitizar os escopos — decisão, não omissão** (achado I1 da revisão adversarial,
 * confirmado por execução). A 1ª versão «limpava» o array (trim + descarte de vazios), e essa
 * defensividade **ALARGAVA o acesso** em dois caminhos:
 *   - `[' financial ']` → o trim fazia `includes('financial')` passar a casar ⇒ **liberava** o que
 *     o pré-B6 negava;
 *   - `['']` → descartar o vazio tornava o grant «ausente» ⇒ **caía na política do papel**, que é
 *     exatamente o alargamento que esta fatia existe para impedir.
 *
 * O escopo é uma CHAVE comparada por igualdade (`roleAllowsAction` → `includes`): normalizá-la é
 * mudar o que casa. O reader repassa o array CRU, como o pré-B6 fazia — byte-identidade de
 * conteúdo. Higienizar o vocabulário é papel da superfície de AUTORIA (o DTO já tem `IsEnum`),
 * nunca do caminho de decisão.
 */
/**
 * O mandato voluntário está VIGENTE? Fail-safe com DIREÇÃO: `now` inválido ou `expiresAt`
 * corrompido ⇒ **não vigente** (molde `withinWindow` da B2 — bound corrompido em aresta que
 * CONCEDE poder nega).
 */
function isInForce(edge, nowMs) {
    if (Number.isNaN(nowMs))
        return false;
    if (edge.expiresAt === null)
        return true;
    const until = Date.parse(edge.expiresAt);
    if (Number.isNaN(until))
        return false;
    return until > nowMs;
}
/**
 * Ordem TOTAL e determinística — **`createdAt` DESC** (o mandato MAIS RECENTE é o operativo),
 * desempate por `grantId` asc. Nunca a UNIÃO (unir alargaria).
 *
 * ★ Por que o mais recente, e por que isto é um DELTA DECLARADO (não monotônico) — achado I1:
 * o pré-B6 usava `findFirst` **sem `orderBy`** ⇒ escolhia por ordem FÍSICA do Postgres, isto é,
 * comportamento **indefinido**; e o schema tem `@@index`, **não** `@@unique`, por (bond, person),
 * com o CRUD (`BondAuthorizedContactsService.add`) sem dedupe ⇒ multi-grant é alcançável. **Não
 * existe claim de monotonia possível contra um baseline indefinido:** numa população multi-grant
 * com escopos distintos, qualquer escolha definida pode conceder algo que a ordem física negava.
 * ⇒ a fatia **declara** o delta em vez de afirmar «nunca libera» (a claim fica restrita a ≤1
 * mandato vigente, onde é provada por enumeração).
 *
 * Entre duas escolhas definidas, a MAIS RECENTE é a mais segura e a mais fiel: se o titular
 * emitiu um mandato novo mais ESTREITO, honrar o mais antigo honraria o mais LARGO e obsoleto.
 * Sem data (`null`) vai para o fim — ausência de data não vence uma autorização datada.
 */
function byDeterministicOrder(a, b) {
    const ca = a.createdAt === null ? Number.NaN : Date.parse(a.createdAt);
    const cb = b.createdAt === null ? Number.NaN : Date.parse(b.createdAt);
    const va = Number.isNaN(ca);
    const vb = Number.isNaN(cb);
    if (!va && !vb && ca !== cb)
        return cb - ca; // DESC — o mais recente primeiro
    if (va !== vb)
        return va ? 1 : -1; // sem data vai para o fim
    return a.grantId < b.grantId ? -1 : a.grantId > b.grantId ? 1 : 0;
}
/**
 * Resolve o mandato voluntário do `invokerPersonId` a partir das arestas cruas.
 *
 * `forfeited` é o produto desta função tanto quanto os escopos: sem ele, o caller não consegue
 * honrar a polaridade `expires-forfeits` sem alargar o acesso (vide docblock do arquivo).
 */
export function resolveVoluntaryGrant(params) {
    const NONE = {
        scopes: [],
        forfeited: false,
        grantId: null,
        validUntil: null,
    };
    if (params.invokerPersonId === null)
        return NONE;
    const mine = params.grants.filter((g) => g.status === 'active' && g.granteePersonId === params.invokerPersonId);
    if (mine.length === 0)
        return NONE;
    const nowMs = Date.parse(params.now);
    const inForce = mine.filter((g) => isInForce(g, nowMs)).sort(byDeterministicOrder);
    // Existe mandato, e NENHUM vigente ⇒ CADUCOU (≠ «não há mandato»).
    const chosen = inForce[0];
    if (chosen === undefined)
        return { scopes: [], forfeited: true, grantId: null, validUntil: null };
    return {
        // Array CRU, sem normalização — vide o bloco «NÃO sanitizar» acima (achado I1).
        scopes: [...chosen.scopes],
        forfeited: false,
        grantId: chosen.grantId,
        validUntil: chosen.expiresAt,
    };
}
/**
 * Resolve o MANDATO completo — quem age por quem, sob qual instrumento, com qual escopo e até
 * quando. `legalAuthority` é o **ECO** do `DecisionAuthority` da B2 (modo + guardianship), nunca
 * um recálculo: os dois eixos coexistem e o log não deve perder nenhum deles.
 */
export function resolveMandate(params) {
    const { subjectRef, invokerRef } = params;
    const actsForAnother = subjectRef !== null && invokerRef !== null && invokerRef.id !== subjectRef.id;
    const viaGuardianshipId = params.legalAuthority?.viaGuardianshipId ?? null;
    const legalRepresents = actsForAnother &&
        params.legalAuthority !== undefined &&
        params.legalAuthority !== null &&
        params.legalAuthority.mode !== 'self';
    if (!actsForAnother) {
        return {
            actsForAnother: false,
            instrument: 'none',
            invokerRef,
            subjectRef,
            grantedScopes: [],
            scopesForfeited: false,
            validUntil: null,
            attribution: { instrument: 'none', viaGrantId: null, viaGuardianshipId },
        };
    }
    const grant = resolveVoluntaryGrant({
        grants: params.grants,
        invokerPersonId: invokerRef.id,
        now: params.now,
    });
    // Precedência do ESCOPO: o mandato voluntário é o instrumento específico; a representação
    // legal é o de fallback. A atribuição preserva AMBOS (o log não perde o coexistente).
    const instrument = grant.grantId !== null ? 'voluntary_grant' : legalRepresents ? 'legal_representation' : 'none';
    return {
        actsForAnother: true,
        instrument,
        invokerRef,
        subjectRef,
        grantedScopes: grant.scopes,
        scopesForfeited: grant.forfeited,
        validUntil: grant.validUntil,
        attribution: { instrument, viaGrantId: grant.grantId, viaGuardianshipId },
    };
}
//# sourceMappingURL=mandate.js.map